import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  toast,
} from "@atlant/ui"

import { FormOwnerSelect } from "#components/form-query/form-owner-select.tsx"
import { graphql } from "#gql"
import type { AircraftSource } from "#gql/graphql.ts"
import { queryKeys } from "#lib/query-keys.ts"
import { client } from "#utils/graphql-client.ts"

const AircraftAddOwnerDocument = graphql(/* GraphQL */ `
  mutation AircraftAddOwner($input: MutationAircraftAddOwnerInput!) {
    aircraftAddOwner(input: $input)
  }
`)

interface AssignOwnerModalProps {
  source: AircraftSource
  aircraftId: string
  trigger?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultOwnerId?: string
}

export const AssignOwnerModal = ({
  source,
  aircraftId,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  defaultOwnerId = "",
}: AssignOwnerModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false)
  const [ownerId, setOwnerId] = useState(defaultOwnerId)
  const queryClient = useQueryClient()

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = (v: boolean) => {
    if (isControlled) controlledOnOpenChange?.(v)
    else setInternalOpen(v)
  }

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () =>
      client.request(AircraftAddOwnerDocument, {
        input: { aircraftId, ownerId, source },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.aircraft.detail(aircraftId),
      })
      await queryClient.invalidateQueries({
        queryKey: queryKeys.aircraft.all(),
      })
      setOwnerId("")
      setOpen(false)
    },
  })

  const handleSubmit = () => {
    if (!ownerId) return
    toast.promise(mutateAsync() as Promise<unknown>, {
      loading: "Assigning owner...",
      success: "Owner assigned!",
    })
  }

  const handleOpenChange = (v: boolean) => {
    setOpen(v)
    if (v) setOwnerId(defaultOwnerId)
    else setOwnerId("")
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Owner</DialogTitle>
          <DialogDescription>Select an owner for this aircraft</DialogDescription>
        </DialogHeader>

        <FormOwnerSelect
          value={ownerId}
          onChange={setOwnerId}
          label="Owner"
          placeholder="Select owner"
        />

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button type="button" variant="outline" className="h-10 flex-1">
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={!ownerId || isPending}
            className="h-10 flex-1"
          >
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
